"use client";

import styles from "./Header.module.css";
import cn from "classnames";
import { IntlShape, useIntl } from "react-intl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ConfigProvider, Drawer, Dropdown, Flex, Menu, MenuProps } from "antd";
import Image from "next/image";
import Link from "next/link";
import { AppNav } from "@/app/app-nav";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useAppContext } from "@/context/AppContext";
import {
  buildPathWithCoin,
  clearSession,
  getCoinFromSearchParams,
  provideSession,
} from "@/app/utils";

import { HeaderProps } from "./Header.props";
import LocaleSwitcher from "../locale-switcher/LocaleSwitcher";
import ThemeSwitcher from "../ThemeSwitcher/ThemeSwitcher";
import { useWindowSize } from "@/hooks/window-size";
import { ItemType } from "antd/es/menu/interface";
import { InfoCircleOutlined } from "@ant-design/icons";
import { logOut } from "@/api/fetcher/component-header-fetcher";
import { AppConstants } from "@/app/constants";

import IconPoolHistory from "@/public/icons/sidebar/icon-pool-history.svg";
import IconConnect from "@/public/icons/sidebar/icon-connect.svg";
import IconHistory from "@/public/icons/sidebar/icon-history.svg";
import IconPayments from "@/public/icons/sidebar/icon-payments.svg";
import IconSettings from "@/public/icons/sidebar/icon-settings.svg";
import IconUsers from "@/public/icons/sidebar/icon-users.svg";
import IconCreateUser from "@/public/icons/sidebar/icon-create-user.svg";
import IconFeePlans from "@/public/icons/sidebar/icon-percent.svg";
import IconCoinsRates from "@/public/icons/sidebar/icon-merge.svg";
import IconPpsState from "@/public/icons/sidebar/icon-bar-chart.svg";
import IconMonitoring from "@/public/icons/sidebar/icon-monitoring.svg";
import IconMain from "@/public/icons/sidebar/icon-home.svg";
import IconAccount from "@/public/icons/header/icon-account.svg";

type MenuItem = Required<MenuProps>["items"][number];
const HEADER_MENU_HINT_DISMISSED_STORAGE_KEY = "ui:header-menu-hint-dismissed";

export default function Header({ ...props }: HeaderProps) {
  const intl = useIntl();
  const router = useRouter();
  const appContext = useAppContext();
  const pathName = usePathname();
  const searchParams = useSearchParams();
  const windowSize = useWindowSize();

  const [openState, setOpenState] = useState(false);
  const [showMenuHint, setShowMenuHint] = useState(false);
  const [menuHintSessionDismissed, setMenuHintSessionDismissed] = useState(false);
  const [selectedPath, setSelectedPath] = useState(
    pathName.replaceAll("/", "")
    // replaceAllPolyfill(pathName, "/", "")
  );
  const menuHintRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const logoutInProgressRef = useRef(false);
  const headerOffsetRef = useRef(0);
  const maxHeaderOffsetRef = useRef(0);
  const lastScrollYRef = useRef(0);
  const latestScrollYRef = useRef(0);
  const frameRequestedRef = useRef(false);

  const openNavDrawer = () => {
    setShowMenuHint(false);
    setMenuHintSessionDismissed(true);
    setOpenState(true);
  };
  const closeNavDrawer = () => setOpenState(false);

  const routeCoin =
    getCoinFromSearchParams(searchParams) ?? appContext.rememberedCoin;
  const buildNavPath = useCallback(
    (path: string) => buildPathWithCoin(`/${path}`, undefined, routeCoin),
    [routeCoin]
  );

  const handleMenuClick = useCallback((path: string) => {
    closeNavDrawer();
    router.push(buildNavPath(path));
  }, [buildNavPath, router]);
  const menuItems = useMemo(
    () =>
      provideMenuItems(
        intl,
        appContext.isAdmin == true,
        appContext.canObserveUsers == true,
        appContext.credentials?.isReadOnly,
        appContext.credentials?.login == AppConstants.SecondaryGuy,
        handleMenuClick
      ),
    [
      appContext.canObserveUsers,
      appContext.credentials?.isReadOnly,
      appContext.credentials?.login,
      appContext.isAdmin,
      handleMenuClick,
      intl,
    ]
  );
  const handleLogout = async () => {
    if (logoutInProgressRef.current) return;
    logoutInProgressRef.current = true;

    const navigateToPublicMain = () => {
      if (typeof window != "undefined") {
        window.location.replace(AppNav.Root);
        return;
      }
      router.replace(AppNav.Root);
    };

    const session = provideSession();
    if (!session) {
      clearSession();
      navigateToPublicMain();
      return;
    }

    await new Promise<void>((resolve) => {
      logOut(
        session,
        () => resolve(),
        (errorStatus) => {
          console.warn(`handleLogout error: ${errorStatus}`);
          resolve();
        }
      );
    });

    clearSession();
    navigateToPublicMain();
  };

  const logoutMenuItems = useMemo(
    () =>
      provideLogoutItems(windowSize.width, appContext.credentials?.login ?? ""),
    [windowSize.width, appContext.credentials?.login]
  );
  const menuHintText =
    (intl.messages["header-menu-hint-text"] as string | undefined) ??
    "Navigation menu is here.";
  const menuHintHideLabel =
    (intl.messages["header-menu-hint-hide"] as string | undefined) ?? "Hide";
  const handleHideMenuHint = useCallback(() => {
    setShowMenuHint(false);
    setMenuHintSessionDismissed(true);
    saveHeaderMenuHintDismissed(HEADER_MENU_HINT_DISMISSED_STORAGE_KEY, true);
  }, []);

  useEffect(() => {
    setSelectedPath(pathName.replaceAll("/", ""));
    // setSelectedPath(replaceAllPolyfill(pathName, "/", ""));
  }, [pathName]);

  useEffect(() => {
    setMenuHintSessionDismissed(false);
  }, [appContext.credentials?.login]);

  useEffect(() => {
    if (windowSize.width > 768) {
      setShowMenuHint(false);
      return;
    }
    if (!appContext.credentials?.login) {
      setShowMenuHint(false);
      return;
    }
    if (menuHintSessionDismissed) {
      setShowMenuHint(false);
      return;
    }
    const dismissed = readHeaderMenuHintDismissed(
      HEADER_MENU_HINT_DISMISSED_STORAGE_KEY
    );
    setShowMenuHint(!dismissed);
  }, [
    appContext.credentials?.login,
    menuHintSessionDismissed,
    windowSize.width,
  ]);

  useEffect(() => {
    if (!showMenuHint) return;

    const closeHintByOutsideTap = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (menuHintRef.current?.contains(target)) return;
      setShowMenuHint(false);
      setMenuHintSessionDismissed(true);
    };

    document.addEventListener("pointerdown", closeHintByOutsideTap);
    return () => {
      document.removeEventListener("pointerdown", closeHintByOutsideTap);
    };
  }, [showMenuHint]);

  useEffect(() => {
    const syncMobileHeaderHeight = (height: number) => {
      if (typeof document == "undefined") return;
      document.documentElement.style.setProperty(
        "--mobile-header-height",
        `${Math.max(0, height).toFixed(2)}px`
      );
    };

    if (windowSize.width > 768) {
      headerOffsetRef.current = 0;
      if (headerRef.current) {
        headerRef.current.style.setProperty("--mobile-header-offset", "0px");
      }
      syncMobileHeaderHeight(0);
      return;
    }
    if (typeof window == "undefined") return;

    const extraHideOffset = 14;

    const applyHeaderOffset = (nextOffset: number) => {
      const clamped = Math.max(
        0,
        Math.min(maxHeaderOffsetRef.current, nextOffset)
      );
      headerOffsetRef.current = clamped;
      if (headerRef.current) {
        headerRef.current.style.setProperty(
          "--mobile-header-offset",
          `${clamped.toFixed(2)}px`
        );
      }
    };

    const recalculateMaxOffset = () => {
      const headerHeight = headerRef.current?.offsetHeight ?? 0;
      syncMobileHeaderHeight(headerHeight);
      maxHeaderOffsetRef.current = Math.max(0, headerHeight + extraHideOffset);
      if (headerOffsetRef.current > maxHeaderOffsetRef.current) {
        applyHeaderOffset(maxHeaderOffsetRef.current);
      }
    };

    const processScroll = (scrollY: number) => {
      const previousScrollY = lastScrollYRef.current;
      const delta = scrollY - previousScrollY;

      if (scrollY <= 0) {
        applyHeaderOffset(0);
        lastScrollYRef.current = 0;
        return;
      }
      if (Math.abs(delta) < 0.5) return;

      applyHeaderOffset(headerOffsetRef.current + delta);
      lastScrollYRef.current = scrollY;
    };

    const handleScroll = () => {
      latestScrollYRef.current = window.scrollY || window.pageYOffset || 0;
      if (frameRequestedRef.current) return;
      frameRequestedRef.current = true;
      window.requestAnimationFrame(() => {
        frameRequestedRef.current = false;
        processScroll(latestScrollYRef.current);
      });
    };

    recalculateMaxOffset();
    lastScrollYRef.current = window.scrollY || window.pageYOffset || 0;
    if (lastScrollYRef.current <= 0) {
      applyHeaderOffset(0);
    }

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", recalculateMaxOffset);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", recalculateMaxOffset);
      frameRequestedRef.current = false;
    };
  }, [windowSize.width]);

  useEffect(() => {
    if (windowSize.width > 768) return;
    headerOffsetRef.current = 0;
    if (headerRef.current) {
      headerRef.current.style.setProperty("--mobile-header-offset", "0px");
    }
  }, [openState, windowSize.width]);

  return (
    <>
      <div ref={headerRef} {...props} className={cn(styles.wrp, props.className)}>
        <Flex vertical={false} wrap="nowrap" className={styles.bar}>
          <Flex vertical={false} wrap="nowrap" className={styles.leftGroup}>
            <div className={styles.menuLauncher}>
              <div
                className={styles.menuIconWrapper}
                onClick={() => openNavDrawer()}
              >
                <Image
                  src="/icons/header/icon-menu-favicon.png"
                  alt=""
                  width={18}
                  height={18}
                  className={styles.menuIconImage}
                  unoptimized
                />
              </div>
              {showMenuHint && (
                <div
                  ref={menuHintRef}
                  className={styles.menuHintBubble}
                  role="dialog"
                  aria-live="polite"
                >
                  <p className={styles.menuHintText}>{menuHintText}</p>
                  <button
                    type="button"
                    className={styles.menuHintAction}
                    onClick={handleHideMenuHint}
                  >
                    {menuHintHideLabel}
                  </button>
                </div>
              )}
            </div>
            <span className={styles.title}>
              <Link className={styles.titleLink} href={buildNavPath(AppNav.Main)}>
                <Image
                  src="/icons/header/icon-menu-logo.svg"
                  alt=""
                  width={35}
                  height={35}
                  className={styles.titleLogo}
                  unoptimized
                />
                {AppConstants.PoolName}
              </Link>
            </span>
          </Flex>
          <Flex vertical={false} wrap="nowrap" className={styles.rightGroup}>
            <ThemeSwitcher />
            <span className={styles.separator}>{"|"}</span>
            <LocaleSwitcher />
            {appContext && appContext.credentials && windowSize.width > 768 && (
              <LogoutText
                userLogin={appContext.credentials.login!}
                logoutMenuItems={logoutMenuItems!}
                logoutCallback={() => handleLogout()}
              />
            )}
            {appContext &&
              appContext.credentials &&
              windowSize.width <= 768 && (
                <LogoutAvatar
                  logoutMenuItems={logoutMenuItems!}
                  logoutCallback={() => handleLogout()}
                />
              )}
          </Flex>
        </Flex>
      </div>
      <div className={styles.drawer}>
        <Drawer
          key="nav-drawer-key"
          title={intl.messages["header-drawer-nav-title"] as string}
          placement="left"
          size="default"
          rootClassName={styles.navDrawer}
          closable={true}
          onClose={closeNavDrawer}
          open={openState}
          width={232}
        >
          <ConfigProvider
            theme={{
              components: {
                Menu: {
                  activeBarBorderWidth: 0,
                },
              },
            }}
          >
            <Menu
              className={styles.navMenu}
              defaultSelectedKeys={[AppNav.Main]}
              mode="inline"
              inlineCollapsed={false}
              selectedKeys={[selectedPath]} //todo: test
              items={menuItems}
            />
          </ConfigProvider>
        </Drawer>
      </div>
    </>
  );
}

function LogoutText({
  userLogin,
  logoutMenuItems,
  logoutCallback,
}: {
  userLogin: string;
  logoutMenuItems: ItemType[];
  logoutCallback: () => void;
}) {
  return (
    <>
      <span className={styles.separator}>{"|"}</span>
      <Dropdown
        menu={{
          items: logoutMenuItems,
          onClick: () => logoutCallback(),
        }}
        trigger={["click"]}
      >
        <div className={styles.userTrigger}>{userLogin}</div>
      </Dropdown>
    </>
  );
}

function LogoutAvatar({
  logoutMenuItems,
  logoutCallback,
}: {
  logoutMenuItems: ItemType[];
  logoutCallback: () => void;
}) {
  return (
    <>
      <span className={styles.separator}>{"|"}</span>
      <Dropdown
        menu={{
          items: logoutMenuItems,
          onClick: () => logoutCallback(),
        }}
        trigger={["click"]}
      >
        <div className={styles.avatarTrigger}>
          <IconAccount />
        </div>
      </Dropdown>
    </>
  );
}

function provideMenuItems(
  intl: IntlShape,
  hasPrivilegedAccess: boolean,
  canObserveUsers: boolean,
  isReadOnly: boolean | undefined,
  isSecondaryGuy: boolean,
  handleMenuClick: (path: string) => void
): MenuItem[] {
  const result: MenuItem[] = [];

  result.push({
    key: AppNav.Main,
    icon: <IconMain />,
    label: intl.messages["nav-main"] as string,
    onClick: (e) => handleMenuClick(e.key),
  });
  result.push({
    key: AppNav.HistoryPool,
    icon: <IconPoolHistory />,
    label: intl.messages["nav-pool-history"] as string,
    onClick: (e) => handleMenuClick(e.key),
  });
  // result.push({
  //   key: AppNav.Rewards,
  //   icon: <IconInfo />,
  //   label: intl.messages["nav-rewards"] as string,
  //   onClick: (e) => handleMenuClick(e.key),
  // });
  result.push({
    key: AppNav.Connect,
    icon: <IconConnect />,
    label: intl.messages["nav-connect"] as string,
    onClick: (e) => handleMenuClick(e.key),
  });
  result.push({
    key: AppNav.Monitoring,
    icon: <IconMonitoring />,
    label: intl.messages["nav-monitoring"] as string,
    onClick: (e) => handleMenuClick(e.key),
  });
  result.push({
    key: AppNav.History,
    icon: <IconHistory />,
    label: intl.messages["nav-history"] as string,
    onClick: (e) => handleMenuClick(e.key),
  });
  result.push({
    key: AppNav.Payouts,
    icon: <IconPayments />,
    label: intl.messages["nav-payouts"] as string,
    onClick: (e) => handleMenuClick(e.key),
  });

  if (isSecondaryGuy || isReadOnly != undefined && !isReadOnly) {
    result.push({
      key: AppNav.Settings,
      icon: <IconSettings />,
      label: intl.messages["nav-settings"] as string,
      onClick: (e) => handleMenuClick(e.key),
    });
  }

  result.push({
    key: AppNav.About,
    icon: <InfoCircleOutlined />,
    label: intl.messages["nav-about"] as string,
    onClick: (e) => handleMenuClick(e.key),
  });

  // result.push({
  //   key: AppNav.Settings,
  //   icon: <IconSettings />,
  //   label: intl.messages["nav-settings"] as string,
  //   onClick: (e) => handleMenuClick(e.key),
  // });
  if (hasPrivilegedAccess || canObserveUsers) {
    result.push({
      type: "divider",
    });
    result.push({
      key: AppNav.Users,
      icon: <IconUsers />,
      label: intl.messages["nav-users"] as string,
      onClick: (e) => handleMenuClick(e.key),
    });
  }

  if (!hasPrivilegedAccess && canObserveUsers) {
    result.push({
      key: AppNav.FeePlan,
      icon: <IconFeePlans />,
      label: intl.messages["nav-fee-plans"] as string,
      onClick: (e) => handleMenuClick(e.key),
    });
  }

  if (hasPrivilegedAccess) {
    const createUserDisabled = isReadOnly == true;
    const feePlanDisabled = isSecondaryGuy;

    result.push({
      key: AppNav.CreateUser,
      icon: <IconCreateUser />,
      label: intl.messages["nav-create-user"] as string,
      disabled: createUserDisabled,
      onClick: createUserDisabled ? undefined : (e) => handleMenuClick(e.key),
    });
    result.push({
      key: AppNav.FeePlan,
      icon: <IconFeePlans />,
      label: intl.messages["nav-fee-plans"] as string,
      disabled: feePlanDisabled,
      onClick: feePlanDisabled ? undefined : (e) => handleMenuClick(e.key),
    });
    result.push({
      key: AppNav.ProfitSettings,
      icon: <IconCoinsRates />,
      label: intl.messages["nav-profit-settings"] as string,
      onClick: (e) => handleMenuClick(e.key),
    });
    result.push({
      key: AppNav.PpsState,
      icon: <IconPpsState />,
      label: intl.messages["nav-pps-state"] as string,
      onClick: (e) => handleMenuClick(e.key),
    });
  }

  return result;
}

function provideLogoutItems(
  windowWidth: number,
  userLogin: string
): MenuProps["items"] {
  const result = [];
  result.push({
    label:
      windowWidth > 768
        ? "Logout"
        : userLogin != ""
        ? `Logout: ${userLogin}`
        : "Logout",
    key: "0",
  });
  return result;
}

function readHeaderMenuHintDismissed(key: string): boolean {
  try {
    return localStorage.getItem(key) == "1";
  } catch {
    return false;
  }
}

function saveHeaderMenuHintDismissed(
  key: string,
  dismissed: boolean
): void {
  try {
    localStorage.setItem(key, dismissed ? "1" : "0");
  } catch {
    // Ignore storage errors for private mode/quota.
  }
}
